import React, { useState, useEffect, useMemo, useRef } from 'react'

import Sidebar from 'components/Sidebar';
import Avatar from 'components/Avatar';
import { useUser } from 'utils/userContext';

import { supabase } from 'utils/supabaseClient';

import * as lessonParser from 'utils/lessonParser';

import Intro from "components/intro/Intro"
import Complete from "components/complete/Complete"
import MultiChoice from "components/challenges/multiChoice/MultiChoice"
import InputBox from "components/challenges/inputBox/InputBox"
import TextBox from "components/challenges/textBox/TextBox"
import MultiChoiceSplit from "components/challenges/multiChoiceSplit/MultiChoiceSplit"
import Match from "components/challenges/match/Match"

// import CodeMirror from '@uiw/react-codemirror'
// import { json } from '@codemirror/lang-json'

function AdminUnits() {
    const [testResultList, setTestResultList] = useState([]);

    const validCheck = useRef(null);

    const testInterval = useRef(null);
    const testTimeout = useRef(null);
    const testError = useRef(null);

    const [testResult, setTestResult] = useState({ tested: false, error: null });

    const { user, loading } = useUser();

    const [openEditor, setOpenEditor] = useState(false);

    const [lessonData, setLessonData] = useState([]);

    const [questionVariables, setQuestionVariables] = useState([]);

    const [questionReady, setQuestionReady] = useState(null);

    const [questionTitle, setQuestionTitle] = useState(<span></span>);

    // const fetchLessonData = async () => {
    //     const { data, error } = await supabase.storage.from('units').download(`lessons.json?t=${Date.now()}`);
    //     if (error) {
    //         console.error("Error fetching lesson data:", error);
    //         return;
    //     }

    //     const concepts = JSON.parse(await data.text());


    //     setLessonData(concepts);
    //     setLessonDataText(JSON.stringify(concepts, null, 2));
    // };

    useEffect(() => {
        let cancelled = false;

        (async () => {
            // const { data, error } = await supabase.storage.from('units').download('lessons.json');
            // if (error) {
            //     console.error("Error fetching lesson data:", error);
            //     return;
            // }
            // use local file for development
            const data = await fetch('/lessons.json');
            if (!data.ok) {
                console.error("Error fetching lesson data:", data.statusText);
                return;
            }


            const concepts = JSON.parse(await data.text());
            if (!cancelled) {
                setLessonData(concepts);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    // escape key to close editor
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpenEditor(false);
                clearTestData();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const [currentLocation, setCurrentLocation] = useState({
        unit: 1,
        concept: 1,
        currentChallenge: 0,
        currentChallengeKey: 1,
    });

    const locationRef = useRef(currentLocation);

    useEffect(() => {
        locationRef.current = currentLocation;
    }, [currentLocation]);

    const currentChallengeData = useMemo(() => {
        return getLesson(lessonData);
    }, [currentLocation, lessonData]);


    function moveNextLesson(lessonData, moveConcept = false, currentLocation = locationRef.current) {
        console.log("Moving to next lesson with current location:", currentLocation);
        const unitIndex = currentLocation.unit - 1;
        const conceptIndex = currentLocation.concept - 1;

        const concepts = lessonData?.[unitIndex]?.concepts;
        if (!concepts) return null;

        const challenges = concepts?.[conceptIndex]?.challenges;
        if (!challenges) return null;


        let previousChallenge = currentLocation.currentChallenge;
        let nextChallenge = getNextChallengeIndex(lessonData, currentLocation, moveConcept);



        setCurrentLocation(prev => ({ ...prev, concept: nextChallenge === "complete" ? prev.concept + 1 : prev.concept, currentChallenge: nextChallenge === "complete" ? 0 : nextChallenge, currentChallengeKey: prev.currentChallengeKey + 1 || 2 }));
        if (nextChallenge === previousChallenge) {
            return null;
        } else {
            return true;
        }
    }


    useEffect(() => {
        if (!lessonData?.length || !currentLocation) return;

        if (currentLocation.currentChallenge === "complete") {
            setQuestionTitle(getConcept(currentLocation, lessonData));
            setQuestionVariables([]);
            setQuestionReady(`${currentLocation.unit}-${currentLocation.concept}-${currentLocation.currentChallenge}-${currentLocation.currentChallengeKey}`);
            return;
        }

        let introExists = lessonData[currentLocation.unit - 1]?.concepts?.[currentLocation.concept - 1]?.intro;
        if (currentLocation.currentChallenge === "intro" && !introExists) {
            console.warn("No intro lesson found for unit:", currentLocation.unit, "concept:", currentLocation.concept);
            const next = moveNextLesson(lessonData);
            return;
        }

        if (!currentChallengeData) return;

        setQuestionReady("");

        if (currentChallengeData.type === "Intro") {
            // Intro lesson, no question
            setQuestionTitle(<span className='text-3xl font-bold'>Lesson Intro</span>);
            setQuestionVariables([]);
            setQuestionReady(`${currentLocation.unit}-${currentLocation.concept}-${currentLocation.currentChallenge}-${currentLocation.currentChallengeKey}`);
            return;
        }

        // ----- compute everything in local vars first -----
        const index = Math.floor(Math.random() * currentChallengeData.question.length);

        const rawVars = currentChallengeData.variables || [];
        const parsedVars = rawVars.length
            ? lessonParser.parseValueList(rawVars, true)
            : [];

        const questionHTML = lessonParser
            .replaceVariablesInText(currentChallengeData.question[index], parsedVars)
            .replace(/\*\*(.*?)\*\*/g, '<span class="questionTitleBolded">$1</span>')
            .replace(/\*(.*?)\*/g, '<span class="questionTitleUnderscore">$1</span>');

        // ----- now push to React in one batch -----
        setQuestionVariables(parsedVars);
        setQuestionTitle(<span dangerouslySetInnerHTML={{ __html: questionHTML }} />);
        setQuestionReady(`${currentLocation.unit}-${currentLocation.concept}-${currentLocation.currentChallenge}-${currentLocation.currentChallengeKey}`);
    }, [currentChallengeData, currentLocation.currentChallengeKey]);

    function getNextChallengeIndex(lessonData, currentLocation, moveConcept = false) {
        if (!lessonData || !lessonData.length) return 0;

        const units = lessonData;
        const currentUnit = units?.[currentLocation.unit - 1];
        const concepts = currentUnit?.concepts;
        const currentConcept = concepts?.[currentLocation.concept - 1];
        const challenges = currentConcept?.challenges;

        if (!challenges?.length) return 0;

        const currentIndex = currentLocation.currentChallenge;
        console.log("Current challenge index:", currentIndex, "for unit:", currentLocation.unit, "concept:", currentLocation.concept);
        if (currentIndex === "intro" || currentIndex === "complete" || typeof currentIndex !== "number") return 0;

        const totalChallenges = challenges.length;

        if (currentIndex >= totalChallenges - 1) {
            if (moveConcept) {
                return "complete";
            }
            return currentIndex
        }
        return currentIndex + 1;
    }

    function getLesson(lessonData) {
        if (!lessonData) return null;

        let fetchingChallenge = currentLocation.currentChallenge;

        // console.log("Fetching challenge for:", currentLocation.unit, currentLocation.concept, currentLocation);

        if (fetchingChallenge === "intro") {
            const concepts = lessonData?.[currentLocation.unit - 1]?.concepts;
            const intro = concepts?.[currentLocation.concept - 1]?.intro;

            if (intro) {
                // show the intro
                return intro;
            } else {
                // no intro, go to first challenge
                const nextChallenge = getNextChallengeIndex(lessonData, currentLocation);
                setCurrentLocation(prev => ({ ...prev, currentChallenge: nextChallenge, currentChallengeKey: prev.currentChallengeKey + 1 }));
                fetchingChallenge = nextChallenge;
            }
        }
        if (fetchingChallenge === "complete") return { type: "Complete", skip: true };
        return lessonData?.[currentLocation.unit - 1]?.concepts?.[currentLocation.concept - 1]?.challenges?.[fetchingChallenge];
    }

    function getConcept({ unit, concept }, lessonData) {
        return lessonData?.[unit - 1]?.concepts?.[concept - 1]?.concept || `Unit ${unit}:${concept}`;
    }



    const challengeComponents = {
        Intro,
        Complete,
        MultiChoice,
        InputBox,
        TextBox,
        MultiChoiceSplit,
        Match,
    };

    let lastKey = useRef(0);
    function getChallengeComponent(questionData, questionText) {
        const Component = challengeComponents[questionData?.type];
        if (lastKey.current !== currentLocation.currentChallengeKey) {
            lastKey.current = currentLocation.currentChallengeKey;
            validCheck.current = null;
        }
        if (Component) {
            return (
                <Component
                    key={currentLocation.unit + "-" + currentLocation.concept + "-" + currentLocation.currentChallenge + "-" + currentLocation.currentChallengeKey}
                    questionData={questionData}
                    questionVariables={questionVariables}
                    questionText={questionText}
                    questionStatus={{ guessed: false, answer: false }}
                    onAnswerCheck={(checkFn) => {

                    }}
                    manualCheck={(force) => {
                    }}
                    onCheckValid={func => validCheck.current = func}
                />
            );
        } else {
            return <div className='w-max text-2xl'>Challenge type "{questionData?.type}" not recognized. Please contact help if this continues.</div>;
        }
    }

    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return;

        const originalConsoleError = console.error;

        console.error = (...args) => {
            if (args[0] === "_K_Test error:") return originalConsoleError(...args);
            let message = args[0];
            if (typeof message === "string" && message.includes("%s")) {
                let i = 1;
                message = message.replace(/%s/g, () => args[i++] ?? "");
            } else {
                message = args.join(" ");
            }
            testError.current = message;

            originalConsoleError(...args);
        };

        testError.current = null;

        return () => {
            console.error = originalConsoleError;
            testError.current = null;
        };
    }, []);

    function clearTestData(reset = true) {
        clearInterval(testInterval.current);
        clearTimeout(testTimeout.current);
        testError.current = null;
        setTestResult({ tested: false, error: null });
        if (reset) {
            setTestResultList([]);
        }
    }

    if (loading || !user?.is_admin) {
        return (
            <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
                <Sidebar />
                <div className="flex-grow h-full bg-background text-text flex items-center justify-center">
                    <h1 className="text-2xl">Access Denied</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0" >
            <Sidebar></Sidebar>
            <div className='Roadmap flex-grow h-full bg-background text-text text-center grid  overflow-hidden' >
                <div className='flex flex-col w-full overflow-x-hidden'>

                    {openEditor === false ? (
                        <div className="flex flex-col items-center justify-start w-full h-full px-4 gap-10">
                            <h1 className='font-bold text-center text-[1.8rem] lg:text-[2rem]'>Units</h1>
                            {lessonData.map((unit, unitIndex) => {
                                return <div key={unit["unit"]} className='px-8 b w-full border-x-2 border-dashed border-border py-5'>
                                    <div className='mb-4 sticky left-8 w-fit bg-background-light z-10 p-4 min-w-64 rounded-xl flex flex-col justify-start'>
                                        <h1 className='text-base font-bold text-text-light w-fit h-fit text-left leading-[0.75] mt-[5px]'>Unit {unitIndex + 1}</h1>
                                        <h1 className='text-xl font-bold w-fit h-fit text-left'>{unit["unit"]}</h1>
                                    </div>
                                    <div className='flex flex-row w-full flex-wrap gap-4'>
                                        {unit.concepts?.map((concept, conceptIndex) => {
                                            return <div key={concept["concept"]} className='flex flex-wrap gap-4'>
                                                <button
                                                    className='border-2 border-border-light text-text px-6 py-4 rounded-md'
                                                    onClick={() => {
                                                        setOpenEditor(conceptIndex);
                                                        clearTestData();
                                                        setCurrentLocation({
                                                            unit: unitIndex + 1,
                                                            concept: conceptIndex + 1,
                                                            currentChallenge: "intro",
                                                            currentChallengeKey: 1
                                                        });
                                                    }}
                                                >
                                                    <h1 className='text-lg font-bold text-left'>{concept["concept"]}</h1>
                                                </button>
                                            </div>
                                        })}
                                    </div>
                                </div>
                            })}
                        </div>
                    ) : (
                        <>
                            <div className='flex items-center justify-between px-6 w-full py-1'>
                                <h1 className='font-bold'>Unit: {currentLocation.unit} Concept: {currentLocation.concept}</h1>
                                <h1 className='font-bold'>{lessonData[currentLocation.unit]?.concepts[currentLocation.concept]?.concept}</h1>
                                <div className='flex items-center gap-1'>
                                    {(testResultList.length && testResultList.findIndex((result) => result.error) !== -1) ? (
                                        <div className={`flex items-center text-warning max-w-[50%] pr-2`}>
                                            <span>
                                                {`${testResultList.map((result, index) => result.error ? (result.concept ? (result.concept + ": " + result.challenge) : index) : null).filter((item) => item !== null).join(", ")}`}
                                            </span>
                                        </div>
                                    ) : null}
                                    <button
                                        className='border-2 border-border text-text px-3 py-1 rounded-md'
                                        onClick={() => {
                                            clearTestData();
                                            setCurrentLocation((prev) => ({
                                                ...prev,
                                                concept: 1,
                                                currentChallenge: 0,
                                                currentChallengeKey: prev.currentChallengeKey + 1
                                            }));
                                            setTestResultList([]);
                                            testInterval.current = setInterval(() => {
                                                if (validCheck.current) {
                                                    let result = validCheck.current();
                                                    if (result) {
                                                        setTestResultList(prev => {
                                                            const updated = [...prev, { tested: true, error: result, concept: locationRef.current.concept, challenge: locationRef.current.currentChallenge }];
                                                            return updated;
                                                        });
                                                        clearTimeout(testTimeout.current);
                                                        let returned = moveNextLesson(lessonData, true);
                                                        if (returned === null) {
                                                            clearInterval(testInterval.current);
                                                            return;
                                                        }
                                                        testError.current = null;
                                                        testTimeout.current = setTimeout(timeoutLoop, 1000);
                                                        return;
                                                    }
                                                }
                                                if (testError.current) {
                                                    console.error("_K_Test error:", testError.current);
                                                    // clearTimeout(testTimeout.current);
                                                    // clearInterval(testInterval.current);
                                                    // setTestResultList((prev) => [...prev, { tested: true, error: testError.current }]);
                                                    let error = testError.current;
                                                    setTestResultList(prev => {
                                                        const updated = [...prev, { tested: true, error, concept: locationRef.current.concept, challenge: locationRef.current.currentChallenge }];
                                                        return updated;
                                                    });
                                                    clearTimeout(testTimeout.current);
                                                    let returned = moveNextLesson(lessonData, true);
                                                    if (returned === null) {
                                                        clearInterval(testInterval.current);
                                                        return;
                                                    }
                                                    testError.current = null;
                                                    testTimeout.current = setTimeout(timeoutLoop, 1000);
                                                    return;
                                                }
                                                setCurrentLocation(prev => ({ ...prev, currentChallengeKey: prev.currentChallengeKey + 1 }));
                                            }, 10);
                                            let timeoutLoop = () => {
                                                setTestResultList(prev => {
                                                    const updated = [...prev, { tested: true, error: null }];
                                                    return updated;
                                                });

                                                let returned = moveNextLesson(lessonData, true);
                                                if (returned === null) {
                                                    clearInterval(testInterval.current);
                                                    return;
                                                }
                                                testError.current = null;
                                                testTimeout.current = setTimeout(timeoutLoop, 1000);

                                            }
                                            testTimeout.current = setTimeout(timeoutLoop, 1000);
                                        }}
                                    >
                                        Test Unit
                                    </button>
                                    <button
                                        className='border-2 border-border text-text px-3 py-1 rounded-md'
                                        onClick={() => {
                                            clearTestData();
                                            setCurrentLocation((prev) => ({
                                                ...prev,
                                                currentChallenge: 0,
                                                currentChallengeKey: prev.currentChallengeKey + 1
                                            }));
                                            setTestResultList([]);
                                            testInterval.current = setInterval(() => {
                                                if (validCheck.current) {
                                                    let result = validCheck.current();
                                                    if (result) {
                                                        setTestResultList(prev => {
                                                            const updated = [...prev, { tested: true, error: result }];
                                                            return updated;
                                                        });
                                                        clearTimeout(testTimeout.current);
                                                        let returned = moveNextLesson(lessonData);
                                                        if (returned === null) {
                                                            clearInterval(testInterval.current);
                                                            return;
                                                        }
                                                        testError.current = null;
                                                        testTimeout.current = setTimeout(timeoutLoop, 2000);
                                                        return;
                                                    }
                                                }
                                                if (testError.current) {
                                                    console.error("_K_Test error:", testError.current);
                                                    // clearTimeout(testTimeout.current);
                                                    // clearInterval(testInterval.current);
                                                    // setTestResultList((prev) => [...prev, { tested: true, error: testError.current }]);
                                                    let error = testError.current;
                                                    setTestResultList(prev => {
                                                        const updated = [...prev, { tested: true, error }];
                                                        return updated;
                                                    });
                                                    clearTimeout(testTimeout.current);
                                                    let returned = moveNextLesson(lessonData);
                                                    if (returned === null) {
                                                        clearInterval(testInterval.current);
                                                        return;
                                                    }
                                                    testError.current = null;
                                                    testTimeout.current = setTimeout(timeoutLoop, 2000);
                                                    return;
                                                }
                                                setCurrentLocation(prev => ({ ...prev, currentChallengeKey: prev.currentChallengeKey + 1 }));
                                            }, 10);
                                            let timeoutLoop = () => {
                                                setTestResultList(prev => {
                                                    const updated = [...prev, { tested: true, error: null }];
                                                    return updated;
                                                });

                                                let returned = moveNextLesson(lessonData);
                                                if (returned === null) {
                                                    clearInterval(testInterval.current);
                                                    return;
                                                }
                                                testError.current = null;
                                                testTimeout.current = setTimeout(timeoutLoop, 2000);

                                            }
                                            testTimeout.current = setTimeout(timeoutLoop, 2000);

                                        }}
                                    >
                                        Test
                                    </button>
                                    <button
                                        className='border-2 border-border text-text px-3 py-1 rounded-md'
                                        onClick={() => {
                                            setOpenEditor(false);
                                            clearTestData();
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                            <div className='w-full h-full px-2'>
                                <div className='flex flex-col p-4 w-full h-full bg-background border-4 border-border-light text-left rounded-xl'>
                                    <div className='flex w-full h-full overflow-y-auto items-center justify-center'>
                                        {questionReady === `${currentLocation.unit}-${currentLocation.concept}-${currentLocation.currentChallenge}-${currentLocation.currentChallengeKey}` ? getChallengeComponent(currentChallengeData, questionTitle) : "No challenge data available."}
                                    </div>
                                    <div className='w-full flex items-center justify-between gap-16'>
                                        <h1 className='font-bold'>{currentLocation.currentChallenge}</h1>
                                        <div className='flex items-center gap-1 w-fit justify-end'>
                                            {testResult.tested && testResult.error && (
                                                <div className={`flex items-center text-warning max-w-[50%] pr-2`}>
                                                    <span>
                                                        {
                                                            setTimeout(() => { console.log("Error report:", questionVariables) }, 0) && <span></span>
                                                        }

                                                        {testResult.error?.includes("Encountered two children with the same key")
                                                            ? "Duplicate key: " + (testResult.error.match(/`([^`]+)`/)?.[1] || "Unknown key")
                                                            : testResult.error}
                                                    </span>                                                </div>
                                            )}
                                            <button
                                                className='border-2 border-border text-text px-3 py-1 rounded-md'
                                                style={{ borderColor: testResult.tested ? (testResult.error ? 'var(--warning)' : 'var(--accent)') : 'var(--border)' }}
                                                onClick={() => {
                                                    clearTestData();
                                                    if (currentLocation.currentChallenge === "intro") return
                                                    console.log("Testing challenge", validCheck.current);
                                                    if (validCheck.current) {
                                                        let result = validCheck.current();
                                                        if (result) {
                                                            setTestResult({ tested: true, error: result });
                                                            return;
                                                        }
                                                    }
                                                    testInterval.current = setInterval(() => {
                                                        if (testError.current) {
                                                            console.error("_K_Test error:", testError.current);
                                                            clearTimeout(testTimeout.current);
                                                            clearInterval(testInterval.current);
                                                            setTestResult({ tested: true, error: testError.current });
                                                            testError.current = null;
                                                            return;
                                                        }
                                                        setCurrentLocation(prev => ({ ...prev, currentChallengeKey: prev.currentChallengeKey + 1 }));
                                                    }, 10);
                                                    testTimeout.current = setTimeout(() => {
                                                        clearInterval(testInterval.current);
                                                        setTestResult({ tested: true, error: null });
                                                    }, 3000);
                                                }}
                                            >
                                                Test
                                            </button>
                                            <button
                                                className='border-2 border-border text-text px-3 py-1 rounded-md'
                                                onClick={() => {
                                                    clearTestData(false);
                                                    if (currentLocation.currentChallenge <= 0) {
                                                        setCurrentLocation(prev => ({ ...prev, currentChallenge: "intro", currentChallengeKey: prev.currentChallengeKey + 1 }));
                                                        return;
                                                    } else if (currentLocation.currentChallenge === "intro") {
                                                        return
                                                    } else {
                                                        setCurrentLocation(prev => ({ ...prev, currentChallenge: prev.currentChallenge - 1, currentChallengeKey: prev.currentChallengeKey + 1 }));
                                                    }

                                                }}
                                            >
                                                Back
                                            </button>
                                            <button
                                                className='border-2 border-border text-text px-3 py-1 rounded-md'
                                                onClick={() => {
                                                    clearTestData(false);

                                                    moveNextLesson(lessonData);
                                                }}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div >
    )
}


export default AdminUnits